import { supabase } from "./supabase";

const LS_KEY = "nigunim:playedSongs";
const PENDING_SYNC_KEY = "nigunim:pendingPlayedSync";

interface PlayedSongEntry {
  songId: string;
  context: string;
  startSec: number;
  playedAt: string;
  cycle: number;
}

function getLocalEntries(): PlayedSongEntry[] {
  return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
}

function saveLocalEntries(entries: PlayedSongEntry[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(entries));
}

export function recordPlayedSong(
  songId: string,
  context: string,
  startSec: number,
  cycle: number = 1
): void {
  const entry: PlayedSongEntry = {
    songId,
    context,
    startSec,
    playedAt: new Date().toISOString(),
    cycle,
  };

  const entries = getLocalEntries();
  entries.push(entry);
  saveLocalEntries(entries);

  supabase.auth.getUser().then(({ data: { user } }) => {
    if (!navigator.onLine && user) {
      const pending = JSON.parse(localStorage.getItem(PENDING_SYNC_KEY) || "[]");
      pending.push({ ...entry, userId: user.id });
      localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(pending));
      return;
    }
    // Record for both registered users and guests
    supabase.from("played_songs").insert({
      user_id: user?.id ?? null,
      song_id: songId,
      context,
      start_sec: startSec,
      cycle,
    }).then(({ error }) => {
      if (error) console.error("Failed to record played song:", error.message);
    });
  });
}

export function getExcludedSongIds(context: string): string[] {
  const entries = getLocalEntries();
  const currentCycle = getCurrentCycle(context);
  return entries
    .filter((e) => e.context === context && e.cycle === currentCycle)
    .map((e) => e.songId);
}

export function getUsedStartSecs(songId: string, context: string): number[] {
  const entries = getLocalEntries();
  return entries
    .filter((e) => e.songId === songId && e.context === context)
    .map((e) => e.startSec);
}

export function getCurrentCycle(context: string): number {
  const entries = getLocalEntries();
  const contextEntries = entries.filter((e) => e.context === context);
  if (contextEntries.length === 0) return 1;
  return Math.max(...contextEntries.map((e) => e.cycle));
}

export function resetCycleIfNeeded(context: string, totalSongs: number): number {
  const currentCycle = getCurrentCycle(context);
  const playedInCycle = getExcludedSongIds(context).length;

  if (playedInCycle >= totalSongs) {
    const newCycle = currentCycle + 1;

    const entries = getLocalEntries();
    const pruned = entries.filter(
      (e) => e.context !== context || e.cycle >= currentCycle
    );
    saveLocalEntries(pruned);

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user || !navigator.onLine) return;
      supabase
        .from("played_songs")
        .delete()
        .eq("user_id", user.id)
        .eq("context", context)
        .lt("cycle", currentCycle)
        .then(({ error }) => {
          if (error) console.error("Failed to prune played_songs:", error.message);
        });
    });

    return newCycle;
  }

  return currentCycle;
}

export async function syncPlayedSongs(): Promise<void> {
  const pending = JSON.parse(localStorage.getItem(PENDING_SYNC_KEY) || "[]");
  if (pending.length === 0) return;

  const synced: number[] = [];
  for (let i = 0; i < pending.length; i++) {
    const { userId, songId, context, startSec, cycle } = pending[i];
    const { error } = await supabase.from("played_songs").insert({
      user_id: userId,
      song_id: songId,
      context,
      start_sec: startSec,
      cycle,
    });
    if (!error) synced.push(i);
  }

  const remaining = pending.filter((_: any, i: number) => !synced.includes(i));
  localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(remaining));
}
